/**
 * Video metadata extraction, provider recognition, and secure embed generation
 * for Learning Assistant and educational media.
 */

export type VideoProvider =
  | 'youtube'
  | 'vimeo'
  | 'aparat'
  | 'instagram'
  | 'tiktok'
  | 'facebook'
  | 'twitter'
  | 'dailymotion'
  | 'twitch'
  | 'direct'
  | 'external';

export type PlaybackMode = 'embed' | 'external';

export interface VideoMetadata {
  originalUrl: string;
  provider: VideoProvider;
  providerName: string;
  videoId?: string;
  embedUrl?: string;
  playbackMode: PlaybackMode;
  suggestedTitle: string;
  isDirectVideo?: boolean;
}

/**
 * Validates whether the URL is a safe HTTPS URL (blocks javascript:, data:, file:, etc.)
 */
export function isSafeVideoUrl(rawUrl: string): boolean {
  if (!rawUrl || typeof rawUrl !== 'string') return false;
  const trimmed = rawUrl.trim();
  if (trimmed.length < 3) return false;
  
  const lower = trimmed.toLowerCase();
  // Reject dangerous script schemes
  if (
    lower.startsWith('javascript:') ||
    lower.startsWith('vbscript:') ||
    lower.startsWith('file:')
  ) {
    return false;
  }

  // Allow data:video and blob: URLs for direct media elements
  if (lower.startsWith('data:video/') || lower.startsWith('blob:')) {
    return true;
  }

  try {
    const parsed = new URL(trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('/') ? (trimmed.startsWith('/') ? `https://localhost${trimmed}` : trimmed) : `https://${trimmed}`);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch (_) {
    return false;
  }
}

/**
 * Extracts 11-char YouTube Video ID strictly from all supported formats:
 * - youtu.be/ID
 * - youtu.be/ID?list=PLAYLIST_ID
 * - youtube.com/watch?v=ID
 * - youtube.com/watch?v=ID&list=PLAYLIST_ID
 * - youtube.com/watch?list=PLAYLIST_ID&v=ID
 * - youtube.com/embed/ID
 * - youtube.com/v/ID
 * - youtube.com/shorts/ID
 * - youtube.com/live/ID
 */
export function extractYouTubeVideoId(url: string): string | null {
  if (!url) return null;
  const cleanUrl = url.trim();
  try {
    const ytMatch = cleanUrl.match(
      /(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:embed\/|v\/|watch\?(?:.*&)?v=|shorts\/|live\/))([a-zA-Z0-9_-]{11})/i
    );
    if (ytMatch && ytMatch[1]) {
      return ytMatch[1];
    }
    if (cleanUrl.includes('youtube.com') || cleanUrl.includes('youtu.be')) {
      const parsed = new URL(cleanUrl.startsWith('http') ? cleanUrl : `https://${cleanUrl}`);
      if (parsed.hostname.includes('youtu.be')) {
        const id = parsed.pathname.slice(1).split('/')[0].split('?')[0];
        if (/^[a-zA-Z0-9_-]{11}$/.test(id)) return id;
      }
      const v = parsed.searchParams.get('v');
      if (v && /^[a-zA-Z0-9_-]{11}$/.test(v)) return v;
    }
  } catch (_) {}
  return null;
}

/**
 * Analyzes video URL, detects provider, video ID, trusted embed URL, and playback mode.
 */
export function parseVideoMetadata(rawUrl: string): VideoMetadata | null {
  if (!isSafeVideoUrl(rawUrl)) return null;

  const clean = rawUrl.trim();
  const lower = clean.toLowerCase();

  // Direct video data URL, blob URL, or API file URL
  if (lower.startsWith('data:video/') || lower.startsWith('blob:') || lower.includes('/api/files/')) {
    return {
      originalUrl: clean,
      provider: 'direct',
      providerName: 'Direct Video',
      playbackMode: 'embed',
      suggestedTitle: 'Direct Media Video',
      isDirectVideo: true
    };
  }

  const url = clean.startsWith('http://') || clean.startsWith('https://') ? clean : `https://${clean}`;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch (_) {
    return null;
  }

  const hostname = parsed.hostname.toLowerCase();
  const pathname = parsed.pathname;

  // Direct video file check (.mp4, .webm, .mov, etc. or R2 storage)
  const isDirectFile = /\.(mp4|webm|mov|m4v|ogg)(\?.*)?$/i.test(pathname) ||
                       hostname.includes('r2.dev') ||
                       hostname.includes('r2.cloudflarestorage.com') ||
                       hostname.includes('storage.googleapis.com');
  if (isDirectFile) {
    const fileName = pathname.split('/').pop()?.split('?')[0] || 'Direct Video';
    return {
      originalUrl: url,
      provider: 'direct',
      providerName: 'Direct Video Stream',
      playbackMode: 'embed',
      suggestedTitle: decodeURIComponent(fileName),
      isDirectVideo: true
    };
  }

  // 1. YouTube
  if (hostname.includes('youtube.com') || hostname.includes('youtu.be') || hostname.includes('youtube-nocookie.com')) {
    const videoId = extractYouTubeVideoId(url);
    if (videoId) {
      return {
        originalUrl: url,
        provider: 'youtube',
        providerName: 'YouTube',
        videoId,
        embedUrl: `https://www.youtube.com/embed/${videoId}`,
        playbackMode: 'embed',
        suggestedTitle: `YouTube Video (${videoId})`
      };
    }
  }

  // 2. Aparat
  if (hostname.includes('aparat.com')) {
    const aparatMatch = pathname.match(/\/v\/([\w-]+)/i);
    const aparatHash = aparatMatch ? aparatMatch[1] : null;
    if (aparatHash) {
      return {
        originalUrl: url,
        provider: 'aparat',
        providerName: 'آپارات (Aparat)',
        videoId: aparatHash,
        embedUrl: `https://www.aparat.com/video/video/embed/videohash/${aparatHash}/vt/frame`,
        playbackMode: 'embed',
        suggestedTitle: `Aparat Video (${aparatHash})`
      };
    }
  }

  // 3. Vimeo
  if (hostname.includes('vimeo.com')) {
    const vimeoMatch = pathname.match(/\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|)(\d+)/);
    const vimeoId = vimeoMatch ? vimeoMatch[1] : null;
    if (vimeoId) {
      return {
        originalUrl: url,
        provider: 'vimeo',
        providerName: 'Vimeo',
        videoId: vimeoId,
        embedUrl: `https://player.vimeo.com/video/${vimeoId}`,
        playbackMode: 'embed',
        suggestedTitle: `Vimeo Video (${vimeoId})`
      };
    }
  }

  // 4. Instagram
  if (hostname.includes('instagram.com') || hostname.includes('instagr.am')) {
    const igMatch = pathname.match(/\/(?:p|reel|reels|tv)\/([A-Za-z0-9_-]+)/i);
    const igId = igMatch ? igMatch[1] : null;
    if (igId) {
      return {
        originalUrl: url,
        provider: 'instagram',
        providerName: 'Instagram',
        videoId: igId,
        embedUrl: `https://www.instagram.com/p/${igId}/embed`,
        playbackMode: 'embed',
        suggestedTitle: `Instagram Reel / Post (${igId})`
      };
    }
  }

  // 5. Dailymotion
  if (hostname.includes('dailymotion.com') || hostname.includes('dai.ly')) {
    let dmId: string | null = null;
    if (hostname.includes('dai.ly')) {
      dmId = pathname.slice(1).split('?')[0];
    } else {
      const dmMatch = pathname.match(/\/video\/([a-zA-Z0-9]+)/);
      if (dmMatch) dmId = dmMatch[1];
    }
    if (dmId) {
      return {
        originalUrl: url,
        provider: 'dailymotion',
        providerName: 'Dailymotion',
        videoId: dmId,
        embedUrl: `https://www.dailymotion.com/embed/video/${dmId}`,
        playbackMode: 'embed',
        suggestedTitle: `Dailymotion Video (${dmId})`
      };
    }
  }

  // 6. TikTok
  if (hostname.includes('tiktok.com')) {
    return {
      originalUrl: url,
      provider: 'tiktok',
      providerName: 'TikTok',
      playbackMode: 'external',
      suggestedTitle: 'TikTok Video'
    };
  }

  // 7. Twitch
  if (hostname.includes('twitch.tv')) {
    const clipMatch = pathname.match(/\/clip\/([a-zA-Z0-9_-]+)/) || pathname.match(/\/([a-zA-Z0-9_-]+)\/clip\/([a-zA-Z0-9_-]+)/);
    if (clipMatch) {
      const clipId = clipMatch[2] || clipMatch[1];
      return {
        originalUrl: url,
        provider: 'twitch',
        providerName: 'Twitch Clip',
        videoId: clipId,
        embedUrl: `https://clips.twitch.tv/embed?clip=${clipId}&parent=${typeof window !== 'undefined' ? window.location.hostname : 'localhost'}`,
        playbackMode: 'embed',
        suggestedTitle: `Twitch Clip (${clipId})`
      };
    }
    return {
      originalUrl: url,
      provider: 'twitch',
      providerName: 'Twitch',
      playbackMode: 'external',
      suggestedTitle: 'Twitch Stream / Video'
    };
  }

  // 8. Facebook
  if (hostname.includes('facebook.com') || hostname.includes('fb.watch')) {
    return {
      originalUrl: url,
      provider: 'facebook',
      providerName: 'Facebook Video',
      playbackMode: 'external',
      suggestedTitle: 'Facebook Video'
    };
  }

  // 9. Twitter / X
  if (hostname.includes('twitter.com') || hostname.includes('x.com')) {
    return {
      originalUrl: url,
      provider: 'twitter',
      providerName: 'X (Twitter)',
      playbackMode: 'external',
      suggestedTitle: 'X (Twitter) Video Post'
    };
  }

  // 10. Generic external HTTPS link
  const domain = hostname.replace(/^www\./, '');
  return {
    originalUrl: url,
    provider: 'external',
    providerName: domain || 'External Video',
    playbackMode: 'external',
    suggestedTitle: `Video Resource (${domain})`
  };
}

/**
 * Returns canonical embed URL for in-page iframe, or null if direct file or external mode.
 */
export function getEmbedVideoUrl(url: string): string | null {
  const meta = parseVideoMetadata(url);
  if (meta && meta.playbackMode === 'embed' && meta.embedUrl) {
    return meta.embedUrl;
  }
  return null;
}
